import { openInlineFancybox } from '@/js/modules/fancybox';

const REVIEW_MODAL_HASH = '#review-modal';
const ROOT_SELECTOR = '.reviews-banner';
const TRIGGER_SELECTOR = `a.banner[href="${REVIEW_MODAL_HASH}"]`;
const TEMPLATE_SELECTOR = '.js-reviews-banner-modal';
const MODAL_BODY_SELECTOR = '.js-reviews-banner-modal-body';
const SWIPE_THRESHOLD_PX = 8;

let reviewModal: HTMLElement | null = null;

function getReviewModal(): HTMLElement | null {
  if (reviewModal && document.body.contains(reviewModal)) {
    return reviewModal;
  }

  const template = document.querySelector<HTMLTemplateElement>(TEMPLATE_SELECTOR);

  if (!template?.content.firstElementChild) {
    return null;
  }

  const modal = template.content.firstElementChild.cloneNode(true) as HTMLElement;

  document.body.appendChild(modal);
  reviewModal = modal;

  return modal;
}

function openReviewModal(trigger: HTMLAnchorElement): void {
  const content = trigger.querySelector<HTMLElement>('.banner-content');

  if (!content) {
    return;
  }

  const modal = getReviewModal();

  if (!modal) {
    return;
  }

  const body = modal.querySelector<HTMLElement>(MODAL_BODY_SELECTOR);

  if (!body) {
    return;
  }

  const clone = content.cloneNode(true) as HTMLElement;
  const description = clone.querySelector('.banner-description');

  // In the card the grid puts the quote above the author, while in the markup
  // the author goes first. Selection follows the markup, not the layout, so in
  // the modal the quote is moved to the front and the grid is not reordering
  // anything - otherwise dragging across the two makes the highlight jump.
  if (description) {
    clone.prepend(description);
  }

  body.replaceChildren(clone);

  void openInlineFancybox(modal, {
    // The quote must stay selectable, so nothing may capture a pointer drag:
    // neither the carousel gestures nor drag-to-close, which sets up its own
    // gesture handler on the viewport.
    dragToClose: false,
    Carousel: {
      gestures: false,
    },
  });
}

function initReviewsBannerRoot(root: HTMLElement): void {
  let pointerStartX = 0;
  let pointerStartY = 0;
  let pointerMoved = false;

  root.addEventListener('pointerdown', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (!event.target.closest(TRIGGER_SELECTOR)) {
      return;
    }

    pointerStartX = event.clientX;
    pointerStartY = event.clientY;
    pointerMoved = false;
  });

  root.addEventListener('pointermove', (event) => {
    if (pointerMoved) {
      return;
    }

    const deltaX = Math.abs(event.clientX - pointerStartX);
    const deltaY = Math.abs(event.clientY - pointerStartY);

    if (deltaX > SWIPE_THRESHOLD_PX || deltaY > SWIPE_THRESHOLD_PX) {
      pointerMoved = true;
    }
  });

  root.addEventListener('click', (event) => {
    if (pointerMoved) {
      return;
    }

    const trigger = event.target instanceof Element
      ? event.target.closest<HTMLAnchorElement>(TRIGGER_SELECTOR)
      : null;

    if (!trigger || !root.contains(trigger)) {
      return;
    }

    event.preventDefault();
    openReviewModal(trigger);
  });
}

export function initReviewsBanner(): void {
  const roots = document.querySelectorAll<HTMLElement>(ROOT_SELECTOR);

  for (const root of roots) {
    initReviewsBannerRoot(root);
  }
}
